import { DateFormat } from '@startuptools/ui/DateFormat';
import { Progress, ProgressStep, ProgressStatus } from '@startuptools/ui/Progress';
import {
  GqlMeetingSummonStatus,
  GqlScriveDocumentStatus,
  GqlZignedAgreementStatus,
} from '../../../../graphql/base-types.graphql';
import { DateTime } from 'luxon';
import { Trans, useLingui } from '@lingui/react';

export const MeetingProgress = ({
  summonStatus,
  meetingDate,
  meetingMinutesReady,
  scriveSignatureStatus,
  zignedAgreementStatus,
}: {
  summonStatus: GqlMeetingSummonStatus;
  meetingDate: DateTime;
  meetingMinutesReady: boolean;
  scriveSignatureStatus: GqlScriveDocumentStatus | null | undefined;
  zignedAgreementStatus: GqlZignedAgreementStatus | null | undefined;
}) => {
  const { i18n } = useLingui();
  const meetingHeld = meetingDate.startOf('day').toMillis() < DateTime.now().startOf('day').toMillis();

  const signatureProgressStatus =
    scriveSignatureStatus === GqlScriveDocumentStatus.Closed ||
    zignedAgreementStatus === GqlZignedAgreementStatus.Fulfilled
      ? ProgressStatus.Done
      : ProgressStatus.Pending;

  return (
    <Progress>
      <ProgressStep
        label={<Trans id="Summon" />}
        status={summonStatus !== GqlMeetingSummonStatus.Pending ? ProgressStatus.Done : ProgressStatus.Pending}
        tooltipText={
          summonStatus === GqlMeetingSummonStatus.Sent ? (
            <Trans id="Summon has been sent" />
          ) : summonStatus === GqlMeetingSummonStatus.Never ? (
            <Trans id="Summon not required" />
          ) : (
            <Trans id="Summon is not yet sent" />
          )
        }
      />
      <ProgressStep
        label={<Trans id="Meeting" />}
        status={meetingHeld ? ProgressStatus.Done : ProgressStatus.Pending}
        tooltipText={
          meetingHeld ? (
            <Trans
              id="Meeting was held on {date}"
              values={{
                date: <DateFormat locale={i18n.locale}>{meetingDate}</DateFormat>,
              }}
            />
          ) : (
            <Trans
              id="Meeting is planned for {date}"
              values={{
                date: <DateFormat locale={i18n.locale}>{meetingDate}</DateFormat>,
              }}
            />
          )
        }
      />
      <ProgressStep
        label={<Trans id="Minutes" />}
        status={meetingMinutesReady ? ProgressStatus.Done : ProgressStatus.Pending}
        tooltipText={
          meetingMinutesReady ? (
            <Trans id="Meeting minutes are complete" />
          ) : (
            <Trans id="Meeting minutes are incomplete" />
          )
        }
      />

      <ProgressStep
        label={<Trans id="Signatures" />}
        status={signatureProgressStatus}
        tooltipText={
          signatureProgressStatus === ProgressStatus.Done ? (
            <Trans id="Meeting is signed by all signatories" />
          ) : (
            <Trans id="Not all signatories have signed the meeting" />
          )
        }
      />
    </Progress>
  );
};
